import { ROUTES } from '@/lib/constants/routes';
import type { MainMenuId, SubMenuId } from '@/config/adminMenuConfig';

/** (mainMenu, subMenu)에 해당하는 관리자 경로. 사이드바/헤더 네비게이션용 */
export function getAdminPathForMenu(mainMenu: MainMenuId, subMenu: SubMenuId): string {
  if (mainMenu === 'customer') {
    if (subMenu === 'organizations') return ROUTES.ADMIN.ORGANIZATIONS;
    if (subMenu === 'users') return ROUTES.ADMIN.USERS;
  }
  if (mainMenu === 'site') {
    if (subMenu === 'resources') return ROUTES.ADMIN.RESOURCES;
    if (subMenu === 'blog') return ROUTES.ADMIN.BLOG;
    if (subMenu === 'faq') return ROUTES.ADMIN.FAQ;
  }
  if (mainMenu === 'sales') {
    if (subMenu === 'orders') return ROUTES.ADMIN.ORDER_MANAGEMENT;
    if (subMenu === 'products') return ROUTES.ADMIN.PRODUCT_MANAGEMENT;
    if (subMenu === 'class') return ROUTES.ADMIN.CLASS_MANAGEMENT;
    if (subMenu === 'scenarios') return ROUTES.ADMIN.SCENARIOS;
  }
  if (mainMenu === 'assets') {
    if (subMenu === 'dialogue') return ROUTES.ADMIN.ASSETS_DIALOGUES;
    if (subMenu === 'task') return ROUTES.ADMIN.ASSETS_TASKS;
    if (subMenu === 'action') return ROUTES.ADMIN.ASSETS_ACTIONS;
    if (subMenu === 'events') return ROUTES.ADMIN.ASSETS_EVENTS;
    if (subMenu === 'symptoms') return ROUTES.ADMIN.ASSETS_SYMPTOMS;
    if (subMenu === 'items') return ROUTES.ADMIN.ASSETS_ITEMS;
  }
  return ROUTES.ADMIN.ORGANIZATIONS;
}

/**
 * /admin 하위 경로를 보고 현재 메인/서브 메뉴를 반환.
 * AdminShellLayout에서 사용.
 */
export function getAdminMenuFromPath(pathname: string): { mainMenu: MainMenuId; subMenu: SubMenuId } {
  const base = pathname.replace(/^\/admin\/?/, '') || 'organizations';

  // 고객관리
  if (base === 'organizations' || base.startsWith('organizations/')) {
    return { mainMenu: 'customer', subMenu: 'organizations' };
  }
  if (base === 'users' || base.startsWith('users')) {
    return { mainMenu: 'customer', subMenu: 'users' };
  }
  if (base === 'resources' || base.startsWith('resources/')) {
    return { mainMenu: 'site', subMenu: 'resources' };
  }
  if (base === 'blog' || base.startsWith('blog/')) {
    return { mainMenu: 'site', subMenu: 'blog' };
  }
  if (base === 'faq' || base.startsWith('faq/')) {
    return { mainMenu: 'site', subMenu: 'faq' };
  }

  // 판매관리 - 주문
  if (base.startsWith('order-management')) {
    return { mainMenu: 'sales', subMenu: 'orders' };
  }
  if (base === 'order-create') {
    return { mainMenu: 'sales', subMenu: 'orders' };
  }

  // 판매관리 - 프로덕트
  if (
    base === 'product-management' ||
    base.startsWith('product/create') ||
    base.startsWith('product/detail/') ||
    base.startsWith('product/edit/')
  ) {
    return { mainMenu: 'sales', subMenu: 'products' };
  }
  if (base.startsWith('products/')) {
    return { mainMenu: 'sales', subMenu: 'products' };
  }

  // 판매관리 - 클래스
  if (
    base === 'class-management' ||
    base.startsWith('class-management/') ||
    base.startsWith('class/create') ||
    base.startsWith('class/edit/') ||
    base.startsWith('open-class/') ||
    base.startsWith('my-classes/')
  ) {
    return { mainMenu: 'sales', subMenu: 'class' };
  }

  // 판매관리 - 시나리오
  if (base === 'scenarios' || base.startsWith('scenarios/')) {
    return { mainMenu: 'sales', subMenu: 'scenarios' };
  }

  // 콘텐츠 관리
  if (base.startsWith('assets/events')) {
    return { mainMenu: 'assets', subMenu: 'events' };
  }
  if (base.startsWith('assets/symptoms')) {
    return { mainMenu: 'assets', subMenu: 'symptoms' };
  }
  if (base.startsWith('assets/tasks')) {
    return { mainMenu: 'assets', subMenu: 'task' };
  }
  if (base.startsWith('assets/actions')) {
    return { mainMenu: 'assets', subMenu: 'action' };
  }
  if (base.startsWith('assets/dialogues')) {
    return { mainMenu: 'assets', subMenu: 'dialogue' };
  }
  if (base.startsWith('assets/items')) {
    return { mainMenu: 'assets', subMenu: 'items' };
  }

  // dashboard 등 기본
  return { mainMenu: 'sales', subMenu: 'orders' };
}

/** 현재 경로에 해당하는 서브 메뉴 id 반환. AdminShellLayout에서 t('admin:subMenus.' + id)로 페이지 타이틀 표시용 */
export function getAdminPageTitleFromPath(pathname: string): SubMenuId | '' {
  const { subMenu } = getAdminMenuFromPath(pathname);
  return subMenu;
}
